import React, { useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import ReactHtmlParser from 'react-html-parser';

import Text from '../Text';
import Icon from '../Icon';

import styles from './SearchBar.module.scss';

import { regExpAccents, regExpAlphanumeric, unicode } from '../../utils/constants';

export type SearchBarOptionsT = {
  text: string;
  onClick: (option: SearchBarOptionsT) => void;
};

type SearchBarT = {
  className?: string;
  size?: 'medium' | 'large';
  value: string;
  onChange: (event: any) => void;
  options?: SearchBarOptionsT[];
  placeholder?: string;
  onPressEnter?: (search: string) => void;
};

function SearchBar({
  value,
  onChange,
  options,
  className,
  size = 'medium',
  placeholder = 'Empieza buscando algo',
  onPressEnter,
}: SearchBarT) {
  const refSearchbard = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState<boolean>(!!value && !!options && !!options.length);

  const searchBarClasses = classnames(styles.searchbar, {
    [styles[`searchbar_${size}`]]: size,
    [`${className}`]: className,
  });

  const formatBoldWords = (optionText: string) => {
    const optionTextClean = optionText.normalize(unicode).replace(regExpAccents, '').toLowerCase();
    const valueClean = value.normalize(unicode).replace(regExpAccents, '').toLowerCase();
    const indexWord = optionTextClean.indexOf(valueClean);
    const firstSection = optionTextClean.slice(0, indexWord);
    const secondSection = optionTextClean.slice(indexWord + value.length + 1);
    const realWord = optionText.slice(indexWord, indexWord + value.length + 1);

    return `${firstSection}<b>${realWord}</b>${secondSection}`;
  };

  const onKeyPressHandler = (event: any, regEx: any) => {
    if (!regEx.test(event.key)) {
      event.preventDefault();
    }
    if (event.key === 'Enter' && !!value) {
      onPressEnter && onPressEnter(value.toLowerCase());
    }
  };

  const onPasteHandler = (event: any, regEx: any) => {
    const textCopied = event.clipboardData.getData('text');
    if (!regEx.test(textCopied)) {
      event.preventDefault();

      return false;
    }
  };

  useEffect(() => {
    setIsOpen(!!value && !!options && !!options.length);
  }, [value, options]);

  useEffect(() => {
    const onClickOut = (event: any) => {
      if (refSearchbard.current && !refSearchbard.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    window.addEventListener('click', onClickOut);

    return () => {
      window.removeEventListener('click', onClickOut);
    };
  }, []);

  return (
    <div ref={refSearchbard} className={searchBarClasses}>
      <Icon name="search" size={size} customClass={styles.searchbar_icon} />
      <input
        type="text"
        className={styles.searchbar_input}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onKeyPress={(event: any) => onKeyPressHandler(event, regExpAlphanumeric)}
        onPaste={(event) => onPasteHandler(event, regExpAlphanumeric)}
      />
      {isOpen && (
        <div className={styles.searchbar_list}>
          <div className={styles.searchbar_list_header_container}>
            <Text customClass={styles.searchbar_list_header} text="Sugerencias" />
          </div>

          <div className={styles.searchbar_list_options}>
            {options?.map((option: SearchBarOptionsT, index: number) => (
              <div
                key={`searchbar_${index}`}
                onClick={() => {
                  option.onClick(option);
                  setIsOpen(false);
                }}
                onKeyPress={() => option.onClick(option)}
                role="button"
                tabIndex={0}
                className={styles.searchbar_list_option}
              >
                {ReactHtmlParser(formatBoldWords(option.text))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default SearchBar;
